'use-strict';

const QuestionProtocol = require('./question-protocol');
const sleepConfig = require('../data/sleep-config.json');
const Protocol = require('./protocol');
const { getBmi } = require('../utils/body');
const { weightedAverage, normalizeReverse0100 } = require('../utils/math');
const { ratio } = require('@hints/utils/math');
const { isEmptyOrNotSet, isset } = require('@hints/utils/data');
const { isBoolean } = require('@hints/utils/types');

const IsiQuestionProtocol = new QuestionProtocol((_evaluation, _tester, answerScore) => normalizeReverse0100(answerScore, 0, 28), sleepConfig.isiQuestions);
const FordQuestionProtocol = new QuestionProtocol((_evaluation, _tester, answerScore) => normalizeReverse0100(answerScore, 9, 36), sleepConfig.fordQuestions);

class SleepProtocol extends Protocol {
    constructor() {
        super();
        this.isiQuestions = IsiQuestionProtocol.questions;
        this.fordQuestions = FordQuestionProtocol.questions;
        this.stopbangQuestions = sleepConfig.stopbangQuestions;

        this.isiRange = IsiQuestionProtocol.range;
        this.fordRange = FordQuestionProtocol.range;
        this.stopRange = { min: 0, max: 4 };
        this.bangRange = { min: 0, max: 4 };
    }

    getScore(evaluation, tester, data) {
        const completedSurveys = data.completedSurveys || [];
        const stopbang = this.computeStopbangScore(completedSurveys, tester, data.stopAnswers);
        const isi = this.computeIsiScore(completedSurveys, evaluation, tester, data.isiAnswers);
        const ford = this.computeFordScore(completedSurveys, evaluation, tester, data.fordAnswers);
        const weights = this.computeScoreWeights(completedSurveys);
        const score = weightedAverage([isi.isiScore, ford.fordScore, stopbang.stopbangScore], [weights.isi, weights.ford, weights.stopbang]);
        return {
            logs: {
                tester,
                ...data,
            },
            data: {
                score: score,
                completedSurveys: data.completedSurveys,
                ...stopbang,
                ...isi,
                ...ford,
            }
        };
    }

    validateResults(body) {
        if (!super.validateResults(body)) return false;
        const completedSurveys = body.completedSurveys;
        if (isEmptyOrNotSet(completedSurveys)) return false;
        const validateIsi = completedSurveys.includes('isi');
        const validateFord = completedSurveys.includes('ford');
        const validateStopbang = completedSurveys.includes('stopbang');
        if (validateIsi && !_validateISI(body)) return false;
        if (validateFord && !_validateFord(body)) return false;
        if (validateStopbang && !_validateStopbang(body)) return false;
        return true;
    }

    /**
     * 
     * @param {any} stopAnswers 
     * @param {import('./protocol').TesterData} tester 
     * @returns 
     */
    computeStopbangScore(completedSurveys, tester, stopAnswers) {
        if (!completedSurveys.includes('stopbang')) return {
            stopbangScore: null,
            stopbangAnswerScore: null,
            stopAnswerScore: null,
            bangAnswerScore: null,
            stopbangResult: null
        };

        const stop = _computeStopScoreAndResult(stopAnswers);
        const bang = _computeBangScoreAndResult(tester);
        const answerScore = stop.score + bang.score;
        const score = normalizeReverse0100(answerScore, 0, 8);

        return {
            stopbangScore: score,
            stopbangAnswerScore: answerScore,
            stopAnswerScore: stop.score,
            bangAnswerScore: bang.score,
            stopbangResult: {
                ...stop.result,
                ...bang.result
            }
        };
    }

    computeIsiScore(completedSurveys, evaluation, tester, isiAnswers) {
        if (!completedSurveys.includes('isi')) return {
            isiScore: null,
            isiAnswerScore: null,
            isiResult: null
        };

        const { data: { answerScore, result, score } } = IsiQuestionProtocol.getScore(evaluation, tester, isiAnswers);
        return {
            isiScore: score,
            isiAnswerScore: answerScore,
            isiResult: result
        };
    }

    computeFordScore(completedSurveys, evaluation, tester, fordAnswers) {
        if (!completedSurveys.includes('ford')) return {
            fordScore: null,
            fordAnswerScore: null,
            fordResult: null
        };

        const { data: { answerScore, result, score } } = FordQuestionProtocol.getScore(evaluation, tester, fordAnswers);
        return {
            fordScore: score,
            fordAnswerScore: answerScore,
            fordResult: result
        };
    }

    computeScoreWeights(completedSurveys) {
        return {
            ford: completedSurveys.includes('ford') ? 1 : 0,
            isi: completedSurveys.includes('isi') ? 1 : 0,
            stopbang: completedSurveys.includes('stopbang') ? 1 : 0,
        };
    }
}

module.exports = new SleepProtocol();

function _computeStopScoreAndResult(stopAnswers) {
    const { isSnoringLoudly, isOftenTiredDuringDay, hasObservedBreathingPause, hasHighBloodPressure } = stopAnswers;
    let score = 0;
    if (isSnoringLoudly) score++;
    if (isOftenTiredDuringDay) score++;
    if (hasObservedBreathingPause) score++;
    if (hasHighBloodPressure) score++;
    return {
        score: score,
        result: {
            isSnoringLoudly: isSnoringLoudly,
            isOftenTiredDuringDay: isOftenTiredDuringDay,
            hasObservedBreathingPause: hasObservedBreathingPause,
            hasHighBloodPressure: hasHighBloodPressure,
        }
    };
}

function _computeBangScoreAndResult(tester) {
    const { weight, height, neck, age, gender } = tester;
    const bmi = getBmi(weight, ratio(height, 100));
    const hasHighBmi = bmi > 35;
    const isOlderAge = age > 50;
    const hasLargeNeck = (gender === 'mr' && neck > 43) || (gender === 'ms' && neck > 41);
    const isMale = gender === 'mr';

    let score = 0;
    if (hasHighBmi) score++;
    if (isOlderAge) score++;
    if (hasLargeNeck) score++;
    if (isMale) score++;
    return {
        score: score,
        result: {
            hasHighBmi: hasHighBmi,
            isOlderAge: isOlderAge,
            hasLargeNeck: hasLargeNeck,
            isMale: isMale,
        }
    };
}

function _validateISI(body) {
    const { isiScore, isiAnswerScore, isiResult } = body;
    return IsiQuestionProtocol.validateResults({ score: isiScore, answerScore: isiAnswerScore, result: isiResult });
}

function _validateFord(body) {
    const { fordScore, fordAnswerScore, fordResult } = body;
    return FordQuestionProtocol.validateResults({ score: fordScore, answerScore: fordAnswerScore, result: fordResult });
}

function _validateStopbang(body) {
    const { stopbangScore, stopAnswerScore, bangAnswerScore, stopbangAnswerScore, stopbangResult } = body;
    if (!isset(stopbangScore) || stopbangScore < 0 || stopbangScore > 100) return false;
    if (!isset(stopAnswerScore) || stopAnswerScore < 0 || stopAnswerScore > 4) return false;
    if (!isset(bangAnswerScore) || bangAnswerScore < 0 || bangAnswerScore > 4) return false;
    if (!isset(stopbangAnswerScore) || stopbangAnswerScore < 0 || stopbangAnswerScore > 8) return false;
    if (!_validateStopResult(stopbangResult)) return false;
    if (!_validateBangResult(stopbangResult)) return false;
    return true;
}

function _validateStopResult(result) {
    if (!isBoolean(result.isSnoringLoudly)) return false;
    if (!isBoolean(result.isOftenTiredDuringDay)) return false;
    if (!isBoolean(result.hasObservedBreathingPause)) return false;
    if (!isBoolean(result.hasHighBloodPressure)) return false;
    return true;
}

function _validateBangResult(result) {
    if (!isBoolean(result.hasHighBmi)) return false;
    if (!isBoolean(result.isOlderAge)) return false;
    if (!isBoolean(result.hasLargeNeck)) return false;
    if (!isBoolean(result.isMale)) return false;
    return true;
}